<template>
  <!-- Side Menu -->
  <aside class="side-menu">
    <nav class="nav flex-column">
      <!-- Home -->
      <router-link class="nav-link" :to="{ name: 'home' }" :class="{ active: isActiveRoute('home') }">
        <NavIcon name="home" class="me-2" />
        {{ $t('sideMenues.home') }}
      </router-link>

      <!-- Events -->
      <router-link class="nav-link" :to="{ name: 'events' }" :class="{ active: isActiveRoute('events') || isActiveRoute('events.show') }">
        <NavIcon name="events" class="me-2" />
        {{ $t('sideMenues.events') }}
      </router-link>

      <!-- Accommodations -->
      <router-link v-if="canViewDetail" class="nav-link" :to="{ name: 'accommodation_bookings' }" :class="{ active: isActiveRoute('accommodation_bookings') }">
        <NavIcon name="accommodations" class="me-2" />
        {{ $t('sideMenues.accommodationBookings') }}
      </router-link>

      <div v-if="canImport || canExport" class="nav-section-title">{{ $t('sideMenues.dataManagement') }}</div>

      <!-- Import with submenu -->
      <div v-if="canImport" class="nav-item-with-submenu">
        <div class="nav-link" :class="{ open: isImportMenuOpen }" @click="toggleImportMenu">
          <i class="bi bi-upload me-2"></i>
          {{ $t('sideMenues.import') }}
          <i class="bi bi-chevron-down ms-auto submenu-arrow" :class="{ rotated: isImportMenuOpen }"></i>
        </div>
        <div class="submenu" :class="{ show: isImportMenuOpen }">
          <router-link class="submenu-link" :to="{ name: 'import_ledghome' }" :class="{ active: isActiveRoute('import_ledghome') }">
            <i class="bi bi-people me-2"></i>
            {{ $t('sideMenues.donatorInformation') }}
          </router-link>
        </div>
      </div>

      <!-- Export with submenu -->
      <div v-if="canExport" class="nav-item-with-submenu">
        <div class="nav-link" :class="{ open: isExportMenuOpen }" @click="toggleExportMenu">
          <i class="bi bi-download me-2"></i>
          {{ $t('sideMenues.export') }}
          <i class="bi bi-chevron-down ms-auto submenu-arrow" :class="{ rotated: isExportMenuOpen }"></i>
        </div>
        <div class="submenu" :class="{ show: isExportMenuOpen }">
          <router-link class="submenu-link" :to="{ name: 'statistic_data' }" :class="{ active: isActiveRoute('statistic_data') }">
            <i class="bi bi-bar-chart me-2"></i>
            {{ $t('sideMenues.statisticData') }}
          </router-link>
        </div>
      </div>

      <!-- Master Data -->
      <router-link v-if="canEdit" class="nav-link" :to="{ name: 'master.event_organizers.index' }" :class="{ active: isActiveRoute('master.event_organizers.index') }">
        <i class="bi bi-database me-2"></i>
        {{ $t('sideMenues.masterData') }}
      </router-link>
    </nav>
  </aside>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useRoute } from 'vue-router';
import { usePermission } from '../../../composables/usePermission';
import NavIcon from './NavIcon.vue';
import { colors } from '../../../../consts/colors';

const route = useRoute();
const isImportMenuOpen = ref(false);
const isExportMenuOpen = ref(false);

// Permission checks
const { hasImportPermission, hasExportPermission, hasEditPermission, hasDetailPermission } = usePermission();

// Make permission checks reactive using computed
const canImport = computed(() => hasImportPermission());
const canExport = computed(() => hasExportPermission());
const canEdit = computed(() => hasEditPermission());
const canViewDetail = computed(() => hasDetailPermission());

/**
 * Toggle import submenu
 */
const toggleImportMenu = () => {
  isImportMenuOpen.value = !isImportMenuOpen.value;
};

/**
 * Toggle export submenu
 */
const toggleExportMenu = () => {
  isExportMenuOpen.value = !isExportMenuOpen.value;
};

/**
 * Check if route is active
 */
const isActiveRoute = (routeName) => {
  return route.name === routeName;
};
</script>

<style lang="scss" scoped>
.side-menu {
  position: fixed;
  top: 54px;
  left: 0;
  width: 250px;
  height: calc(100vh - 54px);
  background-color: v-bind('colors.background');
  overflow-y: auto;
  padding: 16px 0;
  z-index: 1000;

  // Hide side menu on mobile
  @media (max-width: 991.98px) {
    display: none;
  }

  .nav {
    .nav-link {
      padding: 12px 24px;
      color: v-bind('colors.textPrimary');
      font-size: 14px;
      font-weight: 400;
      text-decoration: none;
      display: flex;
      align-items: center;
      transition: all 0.2s;
      border-radius: 8px;

      i {
        font-size: 16px;
      }

      &:hover:not(.disabled) {
        // The menu background is now the page gray, so hover needs its own tone
        background-color: v-bind('colors.gray[200]');
        color: v-bind('colors.btnTextColorHover');
      }

      &.active {
        background-color: v-bind('colors.btnBgColorPrimary');
        color: v-bind('colors.btnTextColorPrimary');
        font-weight: 600;
      }

      &.disabled {
        color: v-bind('colors.gray[400]');
        cursor: not-allowed;
        opacity: 0.6;
      }

      &.open {
        background-color: v-bind('colors.background');
      }
    }

    .nav-section-title {
      padding: 20px 24px 8px 24px;
      color: v-bind('colors.gray[500]');
      font-size: 12px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }

    // Submenu styles
    .nav-item-with-submenu {
      .submenu-arrow {
        font-size: 12px;
        transition: transform 0.2s ease;

        &.rotated {
          transform: rotate(180deg);
        }
      }

      .submenu {
        max-height: 0;
        overflow: hidden;
        transition: max-height 0.3s ease;
        background-color: v-bind('colors.gray[50]');

        &.show {
          max-height: 200px;
        }
      }

      .submenu-link {
        padding: 10px 24px 10px 48px;
        color: v-bind('colors.textPrimary');
        font-size: 14px;
        font-weight: 400;
        text-decoration: none;
        display: flex;
        align-items: center;
        transition: all 0.2s;

        i {
          font-size: 14px;
        }

        &:hover:not(.disabled) {
          background-color: v-bind('colors.gray[200]');
          color: v-bind('colors.blue[500]');
        }

        &.active {
          background-color: v-bind('colors.blue[500]');
          color: v-bind('colors.white');
          font-weight: 600;
        }

        &.disabled {
          color: v-bind('colors.gray[400]');
          cursor: not-allowed;
          opacity: 0.6;
        }
      }
    }
  }
}
</style>
